<template>
  <!-- The payoff (design 2026-10-26), read from the signs the server returned
       on save. variant 'sun': the wheel turns to the Sun sign, Sun / Moon /
       Rising side by side, "Just opened for you", and the gold "add your
       time". variant 'three': the big three after a time and/or place, with
       an honest nudge if anything is still open. Pure: emits more / done. -->
  <div class="po" :class="{ still: reduced }" data-testid="birth-payoff">
    <template v-if="variant === 'sun'">
      <svg class="po-wheel" viewBox="0 0 190 190" aria-hidden="true">
        <defs>
          <radialGradient :id="uid + 'halo'"><stop offset="0" stop-color="#f0d9a3" stop-opacity=".45" />
            <stop offset="1" stop-color="#f0d9a3" stop-opacity="0" /></radialGradient>
        </defs>
        <circle ref="haloEl" class="po-halo" cx="95" cy="95" r="60" :fill="`url(#${uid}halo)`" :opacity="hidden ? 0 : 0.6" />
        <circle class="ring" cx="95" cy="95" r="90" /><circle class="ring" cx="95" cy="95" r="62" />
        <g ref="wheelEl" :style="{ transformOrigin: '95px 95px', transform: `rotate(${-si * 30}deg)` }">
          <text v-for="g in glyphs" :key="g.i" class="gl" :class="{ me: g.i === si }" :x="g.x" :y="g.y">{{ g.g }}</text>
        </g>
        <path d="M95 0 l-6 -10 h12z" fill="#f0d9a3" />
        <text ref="glyphEl" class="center" x="95" y="97" :opacity="hidden ? 0 : 1">{{ si >= 0 ? GLYPHS[si] : '' }}</text>
      </svg>
      <p class="po-eyebrow">{{ COPY.sunEyebrow }}</p>
      <h3 ref="signEl" class="po-sign" data-testid="po-sign" :style="hidden ? { opacity: 0 } : null">{{ sun }}</h3>
      <p v-if="gloss" class="po-gloss">{{ gloss }}</p>
    </template>
    <template v-else>
      <p class="po-eyebrow po-eyebrow-top">{{ COPY.bigThreeEyebrow }}</p>
      <h3 class="po-sign" data-testid="po-heading">{{ COPY.bigThree }}</h3>
      <p class="po-gloss">{{ COPY.bigThreeGloss }}</p>
    </template>

    <div class="po-trio">
      <div v-for="(b, i) in boxes" :key="b.key" class="po-b" :class="[b.state, { arrive: arrives(b) }]"
           :style="{ animationDelay: (0.15 + i * 0.3) + 's' }" :data-testid="'po-b-' + b.key">
        <span class="ic" aria-hidden="true">{{ b.glyph }}</span>
        <span class="k">{{ b.label }}</span>
        <span class="v">{{ b.value }}</span>
        <span class="s">{{ b.sub }}</span>
      </div>
    </div>

    <div v-if="variant === 'sun' && opened.length" class="po-open">
      <p class="po-eyebrow po-eyebrow-left">{{ COPY.openedEyebrow }}</p>
      <div class="po-rows">
        <div v-for="(r, i) in opened" :key="r.key" class="po-row" :class="{ play: !reduced }"
             :style="{ animationDelay: (1.5 + i * 0.25) + 's' }" data-testid="po-row">
          <span class="tk" aria-hidden="true">{{ r.glyph }}</span>
          <div><b>{{ r.title }}</b><span>{{ r.text }}</span></div>
        </div>
      </div>
    </div>

    <p v-if="variant === 'three' && nudge" class="po-nudge" data-testid="po-nudge">{{ nudge.text }}</p>

    <div v-if="actions" class="po-actions">
      <template v-if="variant === 'sun'">
        <button v-if="!complete" type="button" class="po-btn gold" data-testid="po-more" @click="$emit('more')">{{ COPY.addMore }}</button>
        <button type="button" class="po-ghost" data-testid="po-done" @click="$emit('done')">{{ doneLabel }}</button>
      </template>
      <template v-else>
        <button v-if="nudge" type="button" class="po-ghost" data-testid="po-more" @click="$emit('more')">{{ nudge.action }}</button>
        <button type="button" class="po-btn" data-testid="po-done" @click="$emit('done')">{{ doneLabel }}</button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { COPY, GLYPHS, bornGloss, momentComplete, payoffNudge, signIndex, trio } from '@/utils/birth-ask'
import { reducedMotionNow } from '@/composables/useReducedMotion'
import '@/assets/fonts/letter/letter-fonts.css'

const props = defineProps({
  // From the save response: POST /birth/moment/ -> {moment, signs}.
  signs: { type: Object, default: null },
  moment: { type: Object, default: null },
  variant: { type: String, default: 'sun' },          // 'sun' | 'three'
  // What this surface really opened (birth-ask.openedRows).
  opened: { type: Array, default: () => [] },
  doneLabel: { type: String, default: 'Done' },
  // Off once the flow has moved on (the gift room keeps the payoff in the talk).
  actions: { type: Boolean, default: true },
})
defineEmits(['more', 'done'])

const reduced = reducedMotionNow()
const uid = `bmpo${Math.random().toString(36).slice(2, 8)}`
const sun = computed(() => (props.signs && (props.signs.sun || props.signs.sun_text)) || '')
const si = computed(() => signIndex(sun.value))
const gloss = computed(() => bornGloss(props.moment && props.moment.birthdate, sun.value))
const boxes = computed(() => trio(props.signs, props.moment))
const complete = computed(() => momentComplete(props.moment))
const nudge = computed(() => payoffNudge(props.signs, props.moment))
const glyphs = GLYPHS.map((g, i) => {
  const a = (i * 30 - 90) * Math.PI / 180
  return { i, g, x: (95 + 76 * Math.cos(a)).toFixed(1), y: (95 + 76 * Math.sin(a)).toFixed(1) }
})
// In the big three, the boxes the closer look just lit arrive with a glow.
const arrives = (b) => !reduced && props.variant === 'three' && b.key !== 'sun' && b.state !== 'locked'

// The wheel turns to the Sun sign, then the glyph, the halo and the name rise.
const canAnimate = !reduced && typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function'
const hidden = ref(props.variant === 'sun' && canAnimate)
const wheelEl = ref(null); const glyphEl = ref(null); const haloEl = ref(null); const signEl = ref(null)
onMounted(() => {
  if (!hidden.value || !wheelEl.value) return
  const end = -si.value * 30
  wheelEl.value.animate([{ transform: `rotate(${end + 300}deg)` }, { transform: `rotate(${end}deg)` }],
    { duration: 1300, easing: 'cubic-bezier(.2,.75,.2,1)' })
  setTimeout(() => {
    const done = () => { hidden.value = false }
    try {
      if (glyphEl.value) glyphEl.value.animate([{ opacity: 0, transform: 'scale(.5)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 500, fill: 'forwards', easing: 'ease-out' })
      if (haloEl.value) haloEl.value.animate([{ opacity: 0 }, { opacity: 1 }, { opacity: 0.6 }], { duration: 900, fill: 'forwards' })
      const a = signEl.value && signEl.value.animate([{ opacity: 0, transform: 'translateY(8px)', letterSpacing: '.3em' },
        { opacity: 1, transform: 'none', letterSpacing: '.04em' }], { duration: 700, fill: 'forwards', easing: 'ease-out' })
      if (a && a.finished) a.finished.then(done, done); else done()
    } catch (e) { done() }
  }, 1150)
})
</script>

<style scoped>
.po { text-align: center; color: #e9dcff; font-family: 'Letter Serif', 'Cormorant Garamond', Georgia, serif; }
.po-wheel { width: 156px; height: 156px; margin: 4px auto 0; display: block; overflow: visible; }
.ring { fill: none; stroke: rgba(240, 217, 163, 0.25); }
.gl { font: 20px 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', 'DejaVu Sans', sans-serif; fill: #9b86c9;
  text-anchor: middle; dominant-baseline: central; }
.gl.me { fill: #f0d9a3; }
.center { font: 48px 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', 'DejaVu Sans', sans-serif; fill: #f0d9a3;
  text-anchor: middle; dominant-baseline: central; transform-box: fill-box; transform-origin: center; }
.po-eyebrow { margin: 6px 0 0; font: 600 11px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: 0.2em; text-transform: uppercase; color: #9b86c9; }
.po-eyebrow-top { margin-top: 4px; }
.po-eyebrow-left { text-align: left; margin: 0 0 6px; }
.po-sign { margin: 2px 0; font: 600 28px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: 0.04em; color: #f5e8c0; }
.po-gloss { margin: 0; font-size: 18px; line-height: 1.35; font-weight: 500; color: #dccdf5; }
.po-trio { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 16px 0 4px; }
.po-b { border-radius: 14px; padding: 10px 6px 11px; border: 1px solid rgba(195, 169, 255, 0.18); background: rgba(255, 255, 255, 0.03);
  min-height: 96px; display: flex; flex-direction: column; align-items: center; gap: 2px; transition: all 0.5s; box-sizing: border-box; }
.po-b .ic { font: 24px 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', sans-serif; color: #6f5f8e; height: 28px; line-height: 28px; }
.po-b .k { font: 600 10px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: 0.14em; text-transform: uppercase; color: #9b86c9; }
.po-b .v { font-size: 17px; line-height: 1.15; font-weight: 700; color: #f1e7ff; }
.po-b .s { font-size: 13px; line-height: 1.2; font-weight: 500; color: #8f7cb3; }
.po-b.lit { border-color: rgba(240, 217, 163, 0.55); background: rgba(240, 217, 163, 0.08); }
.po-b.lit .ic { color: #f0d9a3; }
.po-b.half { border-color: rgba(240, 217, 163, 0.3); }
.po-b.half .ic { color: #c9b27a; }
.po-b.locked { border-style: dashed; }
.po-b.locked .v { color: #7d6b9c; font-size: 22px; }
.po-b.arrive { animation: arrive 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
@keyframes arrive { 0% { transform: scale(0.85); opacity: 0.2; box-shadow: 0 0 0 0 rgba(240, 217, 163, 0) }
  60% { transform: scale(1.04); opacity: 1; box-shadow: 0 0 26px rgba(240, 217, 163, 0.45) } 100% { transform: none; box-shadow: none } }
.po-open { margin-top: 16px; text-align: left; border-top: 1px solid rgba(195, 169, 255, 0.12); padding-top: 12px; }
.po-row { display: flex; gap: 12px; align-items: center; padding: 8px 2px; }
.po-row .tk { flex: none; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: rgba(240, 217, 163, 0.14); border: 1px solid rgba(240, 217, 163, 0.5); color: #f0d9a3;
  font: 16px 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', sans-serif; }
.po-row b { display: block; font-size: 17px; font-weight: 700; color: #f1e7ff; }
.po-row span { display: block; font-size: 15px; line-height: 1.3; font-weight: 500; color: #9b86c9; }
.po-row.play { animation: rowIn 0.5s ease both; }
@keyframes rowIn { from { opacity: 0; transform: translateX(-8px) } to { opacity: 1; transform: none } }
.po-nudge { margin: 14px 0 0; font-style: italic; font-size: 16px; font-weight: 500; color: #9b86c9; }
.po-actions { display: flex; gap: 10px; justify-content: center; margin-top: 18px; flex-wrap: wrap; }
.po-btn { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: 1px solid rgba(240, 217, 163, 0.35); border-radius: 999px;
  padding: 11px 22px; min-height: 48px; font: 700 18px 'Letter Serif', Georgia, serif; cursor: pointer; box-shadow: 0 8px 24px -10px rgba(123, 75, 176, 0.9); }
.po-btn.gold { background: linear-gradient(180deg, #f3d58f, #d9b25e); color: #2a1845; border: 0; }
.po-ghost { background: transparent; color: #9b86c9; border: 1px solid rgba(155, 134, 201, 0.4); border-radius: 999px; padding: 10px 18px;
  min-height: 48px; font: 600 17px 'Letter Serif', Georgia, serif; cursor: pointer; }
.po-btn:focus-visible, .po-ghost:focus-visible { outline: 2px solid #f0d9a3; outline-offset: 2px; }
@media (min-width: 700px) {
  .po-rows { display: grid; grid-template-columns: 1fr 1fr; gap: 0 12px; }
}
@media (max-width: 600px) {
  .po-actions .po-btn, .po-actions .po-ghost { flex: 1 1 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .po *, .po { animation: none !important; transition: none !important; }
}
.still, .still * { animation: none !important; }
</style>
